/**
 * RequestTypeFilters.tsx
 *
 * Filtri condivisi per le pagine tipologia (Bozzetti, Campionatura, Produzione, Gamma)
 * e per il tab "Ufficio Stile" dello Storico.
 * Espone: ricerca testuale, anno progressivo, stato, intervallo date.
 * Tutti i filtri sono opzionali e confluiscono in un singolo oggetto controllato dal parent.
 */

import React, { useState } from 'react';
import {
  Collapse,
  Group,
  Stack,
  Select,
  Button,
  TextInput,
  Badge,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import {
  IconSearch,
  IconFilter,
  IconX,
  IconChevronDown,
  IconChevronUp,
} from '@tabler/icons-react';
import { RequestStatus } from '../../../types/api';

export interface RequestTypeFiltersValue {
  searchTerm?: string;
  progressiveYear?: number;
  status?: RequestStatus;
  startDate?: string;
  endDate?: string;
}

interface RequestTypeFiltersProps {
  value: RequestTypeFiltersValue;
  onChange: (next: RequestTypeFiltersValue) => void;
  onReset: () => void;
  availableYears?: number[];
  availableStatuses?: RequestStatus[];
  hideStatus?: boolean;
  searchPlaceholder?: string;
}

const STATUS_LABELS: Record<RequestStatus, { label: string; color: string }> = {
  [RequestStatus.New]: { label: 'Nuova', color: 'blue' },
  [RequestStatus.PreCampionatura]: { label: 'In Pre-Campionatura', color: 'grape' },
  [RequestStatus.PreCampionaturaCompleted]: { label: 'Pre-Campionatura Completata', color: 'indigo' },
  [RequestStatus.InProgress]: { label: 'In Lavorazione', color: 'cyan' },
  [RequestStatus.PendingCompletion]: { label: 'In Attesa di Completamento', color: 'yellow' },
  [RequestStatus.Completed]: { label: 'Completata', color: 'green' },
  [RequestStatus.Cancelled]: { label: 'Annullata', color: 'red' },
  [RequestStatus.Suspended]: { label: 'Sospesa', color: 'gray' },
};

const ALL_STATUSES: RequestStatus[] = [
  RequestStatus.New,
  RequestStatus.InProgress,
  RequestStatus.PendingCompletion,
  RequestStatus.Suspended,
  RequestStatus.Completed,
  RequestStatus.Cancelled,
];

export function RequestTypeFilters({
  value,
  onChange,
  onReset,
  availableYears,
  availableStatuses,
  hideStatus,
  searchPlaceholder = 'Cerca per cliente, titolo, note, N. progressivo (es. 5/2025)...',
}: RequestTypeFiltersProps) {
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const statuses = availableStatuses ?? ALL_STATUSES;
  const yearOptions = (availableYears ?? []).map((y) => ({ value: String(y), label: String(y) }));
  const statusOptions = statuses.map((s) => ({
    value: String(s),
    label: STATUS_LABELS[s].label,
  }));

  const activeCount =
    (value.progressiveYear ? 1 : 0) +
    (value.status ? 1 : 0) +
    (value.startDate ? 1 : 0) +
    (value.endDate ? 1 : 0);

  const handleSearch = (next: string) => {
    onChange({ ...value, searchTerm: next || undefined });
  };

  const handleYear = (next: string | null) => {
    onChange({ ...value, progressiveYear: next ? Number(next) : undefined });
  };

  const handleStatus = (next: string | null) => {
    onChange({ ...value, status: next ? (Number(next) as RequestStatus) : undefined });
  };

  const handleStartDate = (next: Date | null) => {
    onChange({ ...value, startDate: next ? next.toISOString() : undefined });
  };

  const handleEndDate = (next: Date | null) => {
    onChange({ ...value, endDate: next ? next.toISOString() : undefined });
  };

  return (
    <Stack gap="sm">
      <Group gap="sm" wrap="nowrap" align="flex-end">
        <TextInput
          flex={1}
          placeholder={searchPlaceholder}
          leftSection={<IconSearch size={16} />}
          value={value.searchTerm ?? ''}
          onChange={(e) => handleSearch(e.currentTarget.value)}
        />
        <Button
          variant="default"
          leftSection={<IconFilter size={16} />}
          rightSection={advancedOpen ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
          onClick={() => setAdvancedOpen((v) => !v)}
        >
          Filtri avanzati
          {activeCount > 0 && (
            <Badge ml="xs" size="sm" circle color="blue">
              {activeCount}
            </Badge>
          )}
        </Button>
        {(value.searchTerm || activeCount > 0) && (
          <Button
            variant="subtle"
            color="gray"
            leftSection={<IconX size={14} />}
            onClick={onReset}
          >
            Reimposta
          </Button>
        )}
      </Group>

      <Collapse in={advancedOpen}>
        <Group gap="sm" align="flex-end" wrap="wrap">
          <Select
            label="Anno"
            placeholder="Tutti gli anni"
            clearable
            data={yearOptions}
            value={value.progressiveYear ? String(value.progressiveYear) : null}
            onChange={handleYear}
            w={140}
          />
          {!hideStatus && (
            <Select
              label="Stato"
              placeholder="Tutti gli stati"
              clearable
              data={statusOptions}
              value={value.status ? String(value.status) : null}
              onChange={handleStatus}
              w={220}
            />
          )}
          <DatePickerInput
            label="Creata dal"
            placeholder="Data inizio"
            clearable
            value={value.startDate ? new Date(value.startDate) : null}
            onChange={handleStartDate}
            w={170}
          />
          <DatePickerInput
            label="Creata al"
            placeholder="Data fine"
            clearable
            value={value.endDate ? new Date(value.endDate) : null}
            onChange={handleEndDate}
            w={170}
          />
        </Group>
      </Collapse>
    </Stack>
  );
}
